/**
 * MarkPaidDialog — confirma a baixa de um lançamento pedindo a DATA real do
 * pagamento (pré-preenchida com hoje, mas editável e aceitando retroativo).
 *
 * Antes disso `useMarkPaid` gravava sempre `paid_date = hoje`, então quem
 * recebeu em janeiro e só deu baixa em março aparecia como recebido em março —
 * o que faria o Relatório de Pagamentos mentir sobre o mês.
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatBRL } from '@/lib/financeLabels';
import { todayIsoDate } from '@/lib/financeStatus';
import type { FinanceEntryWithStatus } from '@/types/database';

interface MarkPaidDialogProps {
  /** Lançamento a dar baixa; `null` mantém o diálogo fechado. */
  entry: FinanceEntryWithStatus | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: (paidDate: string) => void | Promise<void>;
  submitting?: boolean;
}

export function MarkPaidDialog({
  entry,
  onOpenChange,
  onConfirm,
  submitting = false,
}: MarkPaidDialogProps) {
  const [paidDate, setPaidDate] = useState(todayIsoDate());

  // Sugere hoje a cada abertura; o usuário ajusta se a baixa é retroativa.
  useEffect(() => {
    if (entry) setPaidDate(todayIsoDate());
  }, [entry]);

  const isIncome = entry?.kind === 'income';

  const handleConfirm = async () => {
    if (!paidDate) {
      toast.warning(isIncome ? 'Informe a data do recebimento.' : 'Informe a data do pagamento.');
      return;
    }
    await onConfirm(paidDate);
  };

  return (
    <Dialog open={Boolean(entry)} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{isIncome ? 'Marcar como recebido' : 'Marcar como pago'}</DialogTitle>
        </DialogHeader>

        {entry && (
          <div className="space-y-4">
            <div className="rounded-lg border p-3">
              <p className="truncate text-sm font-medium">{entry.description}</p>
              <p
                className={
                  isIncome
                    ? 'text-sm font-semibold text-emerald-600'
                    : 'text-sm font-semibold text-red-600'
                }
              >
                {formatBRL(Number(entry.amount ?? 0))}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="mark-paid-date">
                {isIncome ? 'Data do recebimento' : 'Data do pagamento'}
              </Label>
              <Input
                id="mark-paid-date"
                type="date"
                value={paidDate}
                onChange={(e) => setPaidDate(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Pode ser uma data passada — é ela que define o mês no relatório.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {/* Cancelar NUNCA é desabilitado: se a mutation ficar pendurada (o
              TanStack PAUSA mutations quando o app perde a rede com a tela
              aberta), desabilitar os dois botões prenderia o usuário no modal
              sem saída. Fechar durante a baixa é seguro — o `setToMarkPaid(null)`
              que a mutation faz ao terminar é no-op. */}
          <Button3D color="gray" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button3D>
          <Button3D onClick={handleConfirm} disabled={submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Confirmar
          </Button3D>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
